import { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import style from './Header.module.scss';
import PopUpLayer from '../../../Common/ui-components/PopUpLayer';
import Slider from '../../../Common/ui-components/Slider';
import Button from '../../../Common/ui-components/Button';
import ErrorModal from './ErrorModal';
import { useAdobeLaunch } from '../../../Common/utils/adobeLaunch';
import { RacingContext } from '../../../Home/RacingPage';

const Glossary = ({ rsShowType, isImportanceOpen, setIsImportanceOpen }) => {
    const { t, i18n } = useTranslation();
    const Glossary = window.RaceSimulatorConfig.Glossary[i18n.language];
    const order =
        rsShowType === 'Advanced'
            ? window.RaceSimulatorConfig.ImportanceOrder
            : window.RaceSimulatorConfig.KeyFactorsOrder;

    const onClose = () => {
        setIsImportanceOpen(false);
    };

    return (
        <PopUpLayer
            onClose={onClose}
            title={t('RS_GLOSSARY')}
            isOpen={isImportanceOpen}
            setIsOpen={setIsImportanceOpen}
        >
            <div className={style.listContainer}>
                {order.map((item, index) => (
                    <div key={item} className={style.item}>
                        <span className={style.itemTitle}>{Glossary[item]['title']}</span>
                        <span className={style.itemText}>{Glossary[item]['content']}</span>
                    </div>
                ))}
            </div>
        </PopUpLayer>
    );
};

const Importance = ({
    isEditOpen,
    setIsEditOpen,
    rsImportance,
    setRsImportance,
    reset,
    confirmImportanceValue,
    toastApi
}) => {
    const [isOpenModal, setIsOpenModal] = useState(false);
    const { t, i18n } = useTranslation();
    const confirm = () => {
        const isSuc = confirmImportanceValue();
        if (isSuc) {
            setIsEditOpen(false);
        } else {
            setIsOpenModal(true);
        }
    };

    const onClose = () => {
        reset();
        setIsEditOpen(false);
    };

    return (
        <PopUpLayer onClose={onClose} title={t('RS_IMPORTANCE')} isOpen={isEditOpen} setIsOpen={setIsEditOpen}>
            <div className={`${style.listContainer} ${style.ImportanceListContainer}`}>
                {rsImportance.map((item, i) => (
                    <div key={i} className={style.item}>
                        <span className={style.itemTitle}>{item.title}</span>
                        <span className={`${style.itemText} ${style.itemSlider}`}>
                            <Slider
                                value={item.value}
                                onChange={(val) => {
                                    const newItem = { ...item };
                                    newItem.value = val;
                                    const newRsImportance = [...rsImportance];
                                    newRsImportance[i] = newItem;
                                    setRsImportance(newRsImportance);
                                }}
                                showValueInHandle={true}
                                tooltip={false}
                                segmentedTrackColor={[
                                    '#e8ecf4',
                                    '#d1d8ea',
                                    '#b9c5df',
                                    '#a2b2d5',
                                    '#8b9fcb',
                                    '#748bc0',
                                    '#5d78b6',
                                    '#4565ab',
                                    '#2e51a0',
                                    '#173e96'
                                ]}
                                // segmentedTrack={{
                                //     baseColor: '#173E96',
                                //     useOpacityGradient: true,
                                //     segments: 10
                                // }}
                                min={0}
                                max={10}
                                marks={{
                                    1: '10%',
                                    2: '20%',
                                    3: '30%',
                                    4: '40%',
                                    5: '50%',
                                    6: '60%',
                                    7: '70%',
                                    8: '80%',
                                    9: '90%'
                                }}
                                showMarkLabels={false} // 全局关闭，优先级最高
                            />
                        </span>
                        <span className={style.itemInfluence}>
                            <span>{t('RS_NO_INFLUENCE')}</span>
                            <span>{t('RS_HIGH_INFLUENCE')}</span>
                        </span>
                    </div>
                ))}
            </div>
            <div className={style.buttons}>
                <Button
                    onClick={() => {
                        reset(true);
                        toastApi({ message: t('RS_IMPORTANCE_PAGE_RESET') });
                    }}
                    className={style.reset}
                    variant="outlined"
                >
                    {t('RS_RESET')}
                </Button>
                <Button onClick={confirm} className={style.confirm}>
                    {t('RS_CONFIRM')}
                </Button>
            </div>
            <ErrorModal
                title={t('RS_UNABLE_TO_CONFIRM')}
                content={t('RS_SET_A_NON-ZERO')}
                isOpenModal={isOpenModal}
                setIsOpenModal={setIsOpenModal}
            />
        </PopUpLayer>
    );
};

const Description = ({
    isEditOpen,
    setIsEditOpen,
    rsShowType,
    title,
    text,
    hasEditIcon,
    rsImportance,
    setRsImportance,
    reset,
    confirmImportanceValue,
    toastApi
}) => {
    const [isImportanceOpen, setIsImportanceOpen] = useState(false);
    const { sendAdobeEvent } = useAdobeLaunch();
    const {
        content: { date, venue, raceNo }
    } = useContext(RacingContext);

    const { t, i18n } = useTranslation();
    return (
        <div className={style.KeyFactors}>
            <div className={style.header}>
                <div
                    className={style.titleContainer}
                    onClick={() => {
                        setIsImportanceOpen(true);
                        sendAdobeEvent({
                            event: 'interaction',
                            interaction: { eventID: 'eWinRacing_HorseSelector-Input_ClickGlossary' },
                            content: {
                                category: rsShowType,
                                race_no: `${date?.replaceAll('-', '')}-${venue}-${raceNo}`
                            }
                        });
                    }}
                >
                    <span className={style.title}>{title}</span>
                    <span className={style.icon}></span>
                </div>
            </div>
            <span className={style.text}>{text}</span>
            <Glossary
                rsShowType={rsShowType}
                isImportanceOpen={isImportanceOpen}
                setIsImportanceOpen={setIsImportanceOpen}
            />
            {rsShowType === 'Advanced' && (
                <Importance
                    reset={reset}
                    confirmImportanceValue={confirmImportanceValue}
                    isEditOpen={isEditOpen}
                    setIsEditOpen={setIsEditOpen}
                    rsImportance={rsImportance}
                    setRsImportance={setRsImportance}
                    toastApi={toastApi}
                />
            )}
        </div>
    );
};

const Header = ({
    isEditOpen,
    setIsEditOpen,
    rsShowType,
    rsImportance,
    setRsImportance,
    reset,
    confirmImportanceValue,
    toastApi
}) => {
    const { t } = useTranslation();
    if (rsShowType === 'Advanced') {
        return (
            <Description
                isEditOpen={isEditOpen}
                setIsEditOpen={setIsEditOpen}
                reset={reset}
                confirmImportanceValue={confirmImportanceValue}
                rsShowType={rsShowType}
                title={t('RS_IMPORTANCE')}
                text={t('RS_MODIFY_FACTOR')}
                rsImportance={rsImportance}
                setRsImportance={setRsImportance}
                toastApi={toastApi}
            />
        );
    }
    return <Description rsShowType={rsShowType} title={t('RS_KEY_FACTORS')} text={t('RS_KEY_FACTORS_TEXT')} />;
};
export default Header;
